import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useRef } from 'react';
import Marquee from 'react-fast-marquee';
import { ArrowDownRight, Truck } from 'lucide-react';
import { api, resolveImageUrl, buildWholesaleWhatsAppLink } from '@/lib/api';
import EditorialMarquee from '@/components/EditorialMarquee';
import Manifesto from '@/components/Manifesto';
import ProductCard from '@/components/ProductCard';

const DEFAULT_HEADLINE = ['Wear', 'Your', 'Heritage.'];
const DEFAULT_SUBLINE = 'A quiet house of Nepali cultural clothing — Tibetan Bakhu, Tamang, Gurung, Rai, Limbu, Newari, Lepcha, Dhaka, Bhutanese and more. Ready-to-wear or crafted to your measurements.';

const lineVariant = {
  hidden: { y: '110%' },
  show: (i) => ({ y: '0%', transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] } }),
};

function Hero({ heroImage, headline, subline }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '-18%']);
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  const words = (headline?.trim() ? headline.trim().split(/\s+/) : DEFAULT_HEADLINE).slice(0, 4);
  const src = resolveImageUrl(heroImage);
  const hasHero = !!src;

  return (
    <section ref={ref} className="relative w-full overflow-hidden bg-[color:var(--mb-bg)]" data-testid="hero-section">
      {/* Faint parallax brand mark */}
      <motion.img style={{ y: bgY }} src="/brand/mark.jpg" alt="" aria-hidden className="absolute -right-24 top-32 w-[65vw] max-w-[900px] opacity-[0.06] pointer-events-none select-none" />

      {/* Mobile-first: hero image on top (only when set), then text */}
      {hasHero && (
        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full pt-24 lg:hidden"
          data-testid="hero-mobile-cover"
        >
          <div className="w-full max-h-[70vh] bg-[color:var(--mb-bg-alt)] border-y border-[color:var(--mb-border)] flex items-center justify-center overflow-hidden">
            <img
              src={src}
              alt="Mahabir Bastralaya cover"
              className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
              data-testid="hero-cover-image-mobile"
            />
          </div>
        </motion.div>
      )}

      <div className={`relative z-10 max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-8 items-end px-6 sm:px-10 pb-16 ${hasHero ? 'pt-16 lg:pt-40' : 'pt-40'} lg:min-h-screen`}>
        <div className="lg:col-span-7 flex flex-col justify-end">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="flex items-center gap-4 mb-8">
            <span className="h-px w-16 bg-[color:var(--mb-primary)]" />
            <p className="eyebrow">Est. 1999 · Nepali Heritage House</p>
          </motion.div>

          <h1 className="font-serif-display text-[15vw] sm:text-[13vw] lg:text-[10vw] leading-[0.85] tracking-[-0.02em] text-[color:var(--mb-text)]">
            {words.map((line, i) => (
              <span key={i + line} className="reveal-mask block">
                <motion.span
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  custom={i}
                  className={`inline-block ${i === words.length - 1 ? 'italic text-[color:var(--mb-primary)]' : ''}`}
                  style={{ fontWeight: i === words.length - 1 ? 400 : 300 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.1 }} className="mt-10 max-w-xl text-lg text-[color:var(--mb-text-muted)] font-light leading-relaxed">
            {subline?.trim() || DEFAULT_SUBLINE}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.35 }} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/products" data-testid="hero-explore-cta" className="btn-red">
              Explore Collection <ArrowDownRight size={16} />
            </Link>
            <Link to="/about" data-testid="hero-about-cta" className="btn-outline">Our Story</Link>
          </motion.div>

          {/* Ships across India — subtle */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.6 }} className="mt-8 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]" data-testid="ships-across-india">
            <Truck size={14} className="text-[color:var(--mb-primary)]" />
            Ships across India
          </motion.div>
        </div>

        {/* Desktop only — arched hero image */}
        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative hidden lg:block"
        >
          <div className="arch-top aspect-[3/4] w-full max-w-[440px] ml-auto bg-[color:var(--mb-bg-alt)] border border-[color:var(--mb-border)] overflow-hidden flex items-center justify-center">
            {hasHero ? (
              <img
                src={src}
                alt="Mahabir Bastralaya cover"
                className="max-w-full max-h-full w-auto h-auto object-contain"
                data-testid="hero-cover-image-desktop"
              />
            ) : (
              <div className="text-center px-8">
                <img src="/brand/mark.jpg" alt="" className="w-24 h-auto mx-auto opacity-70" style={{ mixBlendMode: 'multiply' }} />
                <p className="mt-4 text-xs tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]">Upload a cover from the admin panel</p>
              </div>
            )}
          </div>
          <div className="absolute -bottom-4 -left-2 flex flex-col gap-1">
            <span className="eyebrow">Since</span>
            <span className="font-serif-display italic text-4xl text-[color:var(--mb-primary)]">1999</span>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2">
        <span className="eyebrow text-[0.6rem]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} className="w-px h-10 bg-gradient-to-b from-[color:var(--mb-primary)] to-transparent" />
      </motion.div>
    </section>
  );
}

function ShowcaseReel({ images = [] }) {
  if (!images || images.length === 0) return null;
  const list = images.map(resolveImageUrl);
  const doubled = [...list, ...list]; // seamless loop
  return (
    <section className="py-8 sm:py-12 bg-[color:var(--mb-bg-alt)] border-y border-[color:var(--mb-border)] overflow-hidden" data-testid="showcase-reel">
      <div className="px-6 sm:px-10 max-w-[1600px] mx-auto mb-6 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="eyebrow">The Showcase</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight mt-1">On the loom this season.</h2>
        </div>
        <Link to="/products" className="text-[0.7rem] tracking-[0.3em] uppercase text-[color:var(--mb-primary)] hover:underline">See all →</Link>
      </div>
      <Marquee speed={35} gradient={false} pauseOnHover>
        {doubled.map((src, i) => (
          <div key={i} className="mx-3 h-[320px] sm:h-[440px] w-[240px] sm:w-[340px] overflow-hidden bg-[color:var(--mb-surface)] border border-[color:var(--mb-border)]">
            <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
          </div>
        ))}
      </Marquee>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [home, setHome] = useState({ hero_image: null, hero_headline: '', hero_subline: '', showcase_images: [], manifesto_images: [] });

  useEffect(() => {
    api.get('/products', { params: { featured: true } }).then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get('/categories').then((r) => setCategories(r.data || [])).catch(() => {});
    api.get('/home').then((r) => setHome(r.data || {})).catch(() => {});
  }, []);

  const allCats = categories.filter((c) => c.slug !== 'made-to-order');
  const categoryMap = Object.fromEntries(categories.map((c) => [c.slug, c]));

  return (
    <div data-testid="home-page">
      <Hero heroImage={home.hero_image} headline={home.hero_headline} subline={home.hero_subline} />
      <ShowcaseReel images={home.showcase_images || []} />
      <EditorialMarquee />

      {/* All categories */}
      <section className="py-24 sm:py-32 px-6 sm:px-10 max-w-[1600px] mx-auto" data-testid="categories-section">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
          <div>
            <p className="eyebrow mb-4">Cultural Categories</p>
            <h2 className="font-serif-display text-5xl sm:text-6xl tracking-tight leading-[0.95] max-w-3xl">
              A <em className="text-[color:var(--mb-primary)] not-italic">tapestry</em> of Nepali traditions.
            </h2>
          </div>
          <Link to="/products" data-testid="view-all-collection" className="btn-outline">View All</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="categories-grid">
          {allCats.map((c, i) => {
            const cover = resolveImageUrl(c.cover_image);
            return (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={`/products?category=${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group block product-card h-full"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-[color:var(--mb-bg-alt)] flex items-center justify-center">
                    {cover ? (
                      <img src={cover} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="text-center px-4">
                        <p className="font-serif-display italic text-6xl text-[color:var(--mb-primary)]/25 leading-none">
                          {String(c.order).padStart(2, '0')}
                        </p>
                      </div>
                    )}
                    {c.subcategories?.length > 0 && (
                      <span className="absolute top-2 right-2 bg-white/85 backdrop-blur-sm text-[0.55rem] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full text-[color:var(--mb-text)]">
                        {c.subcategories.length} styles
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col gap-1">
                    <p className="eyebrow text-[0.6rem]">Ch. {String(c.order).padStart(2, '0')}</p>
                    <h3 className="font-serif-display text-xl sm:text-2xl leading-tight tracking-tight group-hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 300ms ease' }}>
                      {c.name}
                    </h3>
                    {c.description && (
                      <p className="text-xs text-[color:var(--mb-text-muted)] line-clamp-2">{c.description}</p>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Made to Order banner */}
      <section className="px-6 sm:px-10 max-w-[1600px] mx-auto pb-16" data-testid="made-to-order-banner">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="bg-[color:var(--mb-primary)] text-white p-10 sm:p-16 grid lg:grid-cols-12 gap-8 items-center rounded-sm"
        >
          <div className="lg:col-span-8">
            <p className="text-white/70 text-[0.7rem] tracking-[0.35em] uppercase mb-4">Our Speciality</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Made to Order,<br/><em>crafted to you.</em>
            </h2>
            <p className="mt-6 max-w-xl text-white/85 font-light leading-relaxed">
              Every community, every silhouette, every occasion. Choose your fabric, share your measurements, and our master tailors will craft it — from the first thread onward. Delivered anywhere in India.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/products?category=made-to-order" data-testid="mto-cta" className="inline-flex items-center gap-3 bg-white text-[color:var(--mb-primary)] px-8 py-4 rounded-full text-[0.7rem] tracking-[0.3em] uppercase font-medium hover:bg-[color:var(--mb-bg)]" style={{ transition: 'background-color 250ms ease' }}>
              Start a Custom Order →
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="py-16 sm:py-24 px-6 sm:px-10 max-w-[1600px] mx-auto" data-testid="featured-section">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-4">The Signature Edit</p>
              <h2 className="font-serif-display text-5xl sm:text-6xl tracking-tight leading-[0.95] max-w-2xl">
                <em>Featured</em> pieces,<br/>curated this season.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {featured.slice(0, 6).map((p, i) => (
              <ProductCard product={p} key={p.id} index={i} categoryMap={categoryMap} />
            ))}
          </div>
        </section>
      )}

      <Manifesto images={home.manifesto_images || []} />

      {/* Wholesale section — minimal but proper, below the story sections */}
      <section className="px-6 sm:px-10 max-w-[1600px] mx-auto pb-8" data-testid="wholesale-section">
        <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-bg-alt)] grid lg:grid-cols-12 gap-0 overflow-hidden">
          <div className="lg:col-span-6 p-8 sm:p-12 flex flex-col justify-center">
            <p className="eyebrow mb-4">Trade / Bulk Orders</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[0.95] tracking-tight">
              We deal in <em className="text-[color:var(--mb-primary)] not-italic">wholesale</em> too.
            </h2>
            <p className="mt-5 max-w-lg text-[color:var(--mb-text-muted)] font-light leading-relaxed">
              Boutiques, cultural organisations and community stores — we supply our traditional Nepali attires in bulk at wholesale rates. Reach out on WhatsApp for a wholesale catalogue and pricing.
            </p>
            <div className="mt-7">
              <a
                href={buildWholesaleWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="wholesale-cta"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-full text-[0.7rem] tracking-[0.25em] uppercase font-medium hover:brightness-95"
                style={{ transition: 'filter 200ms ease' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.87 11.87 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.89c0 2.1.55 4.14 1.6 5.94L0 24l6.32-1.66a11.86 11.86 0 0 0 5.74 1.46h.01c6.56 0 11.89-5.34 11.89-11.9a11.83 11.83 0 0 0-3.44-8.42Zm-8.46 18.3h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.24-.38a9.86 9.86 0 0 1-1.51-5.25c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.13 1.03 6.99 2.9a9.83 9.83 0 0 1 2.9 6.99c0 5.46-4.44 9.88-9.89 9.88Z"/></svg>
                Enquire Wholesale
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 min-h-[280px] bg-[color:var(--mb-surface)] border-t lg:border-t-0 lg:border-l border-[color:var(--mb-border)]">
            {(home.wholesale_images || []).length > 0 ? (
              <div className="grid grid-cols-2 h-full gap-px bg-[color:var(--mb-border)]">
                {(home.wholesale_images || []).slice(0, 4).map((img, i) => (
                  <div key={i} className="bg-[color:var(--mb-surface)] overflow-hidden aspect-square">
                    <img src={resolveImageUrl(img)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full min-h-[280px] flex items-center justify-center text-center px-6">
                <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]">Add wholesale photos from the admin panel</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Ships across India — retail & made-to-order only */}
      <section className="px-6 sm:px-10 max-w-[1600px] mx-auto pb-16" data-testid="shipping-strip">
        <div className="border-y border-[color:var(--mb-border)] py-6 flex items-center gap-3 text-sm justify-center text-center">
          <Truck size={16} className="text-[color:var(--mb-primary)] shrink-0" />
          <p className="text-[color:var(--mb-text-muted)]">
            <span className="text-[color:var(--mb-text)]">We ship across India</span> — every retail &amp; made-to-order piece delivered to your doorstep, wherever you are in the country.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 px-6 sm:px-10 max-w-[1600px] mx-auto text-center border-t border-[color:var(--mb-border)]">
        <p className="eyebrow mb-6">Visit us in Jaigaon</p>
        <h2 className="font-serif-display text-5xl sm:text-7xl tracking-tight leading-[0.95] max-w-3xl mx-auto">
          Step into the <em className="text-[color:var(--mb-primary)] not-italic">atelier</em>.
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-[color:var(--mb-text-muted)] font-light">
          Come by our store on NS Road, Jaigaon. Tuesday to Sunday, 10 AM to 8 PM.
        </p>
        <div className="mt-10 flex items-center gap-4 justify-center flex-wrap">
          <Link to="/contact" data-testid="cta-visit" className="btn-red">Get Directions</Link>
          <Link to="/products" data-testid="cta-browse" className="btn-outline">Browse Collection</Link>
        </div>
      </section>
    </div>
  );
}
